require("./brokerCryptoModalDocumentation.scss");

// @vendors
import { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
const { formatText, FormattedText } = require('core/i18n').i18n;
// @utils
import { stopPropagation } from "./utils/brokerCryptoModalDocumentation.utils";
import { base64ToBlob, downloadAsFile } from 'utilities/APIRequestHelper';
import BrowserUtils from 'utilities/browserUtils';
import { taggingEventHandled } from 'utilities/taggingEventHandled';
import { recordPage } from "utilities/tagging";
// @components
import { CryptoModalDocumentationHeader } from "./components/header/brokerCryptoModalDocumentationHeader";
import { CryptoModalDocumentationBody } from "./components/body/brokerCryptoModalDocumentationBody";
import { CryptoButton } from "../brokerCryptoButton/brokerCryptoButton";
import useFocusTrap from "../components/hooks/useFocusTrap";
// @ commons
import { OKPdfViewer } from "commonsComponents/OKPdfViewer";
import { LoadingSection } from 'commonsComponents/loadingSection';
// @constants
import { CONTENT_TYPE_PDF } from "constants/index"
import { PROC_BROKER_CONVENIENCE_CRYPTO_TEST } from 'utilities/tagging';

const CryptoModalDocumentation = ({
    isOpen,
    onClose,
    immBrokerConvenienceMiFIDTest,
    goToPurchaseFlow,
    getCryptoDocumentation
}) => {
    const [documentReaded, setDocumentReaded] = useState(false);
    const modalRef = useRef(null);
    useFocusTrap(isOpen, modalRef);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = "hidden";

            const customData = {
                pageName: "/documentationAboutCryptocurrencies",
                sectionFamily: "wealth",
                sectionCategory: "services",
                sectionName: "crypto",
                interactionType: "page",
                serviceType: "productInformation"
            };
            recordPage(customData);

            if (
                immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'base64String']) === null
                && !immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'cryptoDocumentationLoading']
            )) {
                getCryptoDocumentation();
            }
        } else {
            document.body.style.overflow = "auto";
        }

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [isOpen]);

    const getBase64SizeInKB = (base64String) => {
        const base64WithoutPrefix = base64String.includes(',')
            ? base64String.split(',')[1]
            : base64String;
        const sizeInBytes = (base64WithoutPrefix.length * 0.75);
        return Math.round((sizeInBytes / 1024) * 100) / 100;
    };

    const documentSizeKB = immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'base64String']) ? getBase64SizeInKB(
        immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'base64String'])
    ) : null;

    const handlePrintPdf = () => {
        taggingEventHandled(PROC_BROKER_CONVENIENCE_CRYPTO_TEST ,'modalDocumentationPrint');
        const documentContent = immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'base64url'])
        if (BrowserUtils.browserIsIE()) {
            const data = [base64ToBlob(documentContent)];
            const blobObject = new Blob(data, {
                type: `${CONTENT_TYPE_PDF};base64`,
            });
            window.navigator.msSaveOrOpenBlob(blobObject);
        } else {
            const winparams = `dependent=yes,locationbar=no,scrollbaars=yes,menubar=yes,resizable,screenX=50,screenY=50,width=${screen.width},height=${screen.height}`;
            const printWindow = window.open(documentContent, '', winparams);
            printWindow.print();
        }
    };

    const handlePDFDownload = () => {
        taggingEventHandled(PROC_BROKER_CONVENIENCE_CRYPTO_TEST ,'modalDocumentationDownload');
        downloadAsFile(
            base64ToBlob(immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'base64String'])),
            CONTENT_TYPE_PDF,
            `${formatText('brokerCryptoTest-downloadFileName')}.pdf`, //`testDocument.pdf`,
        );
    }

    const handlePrimaryButtonClick = () => {
        taggingEventHandled(PROC_BROKER_CONVENIENCE_CRYPTO_TEST ,'modalDocumentationSign');
        goToPurchaseFlow();
    }

    const containerClass = isOpen
        ? "modal_documentation__container modal_documentation__container--open"
        : "modal_documentation__container modal_documentation__container--closed";

    const overlayClass = isOpen
        ? "modal_documentation__overlay modal_documentation__overlay--open"
        : "modal_documentation__overlay modal_documentation__overlay--closed";

    const renderModalContent = (children) => (
        <div className="modal_documentation" ref={modalRef}>
            <div className={containerClass} onClick={stopPropagation}>
                {children}
            </div>
            <div className={overlayClass} onClick={onClose} />
        </div>
    );

    if (!isOpen) {
        return null;
    }

    const content = immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'cryptoDocumentationLoading'])
        ? renderModalContent(<LoadingSection />)
        : renderModalContent(
            <>
                <CryptoModalDocumentationHeader
                    onClose={onClose}
                    title={formatText('brokerCryptoTest-pfdViewerTitle')}
                    subtitle={`PDF (${documentSizeKB}KB)`}
                    onPrint={handlePrintPdf}
                    onDownload={handlePDFDownload}
                />
                <CryptoModalDocumentationBody>
                    <OKPdfViewer
                        pdfFile={immBrokerConvenienceMiFIDTest.getIn(['test', 'cryptoDocumentation', 'base64String'])}
                        showScroll={true}
                        viewerHeight={700}
                        onDocumentReaded={() => setDocumentReaded(true)}
                    />
                </CryptoModalDocumentationBody>
                <div className="modal_documentation__button-container">
                    {documentReaded && (
                        <CryptoButton
                            extraClass="modal_documentation__button"
                            onClick={handlePrimaryButtonClick}
                            text={formatText('brokerCryptoTest-pfdViewerButton')}
                        >
                            <FormattedText value="brokerCryptoTest-documentationModalButton" />
                        </CryptoButton>
                    )}
                </div>
            </>
        );

    return content;
}

CryptoModalDocumentation.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    goToPurchaseFlow: PropTypes.func.isRequired,
    immBrokerConvenienceMiFIDTest: PropTypes.object.isRequired,
    getCryptoDocumentation: PropTypes.func.isRequired,
};

export { CryptoModalDocumentation };
